import { Account, GROUPS } from "./accounts";
import { SysadminData } from "./SysadminData";

export type IssueAction =
    | { kind: "editCompany"; id: string }
    | { kind: "addOffice" }
    | { kind: "editOffice"; id: string }
    | { kind: "addEaterGroup"; officeId: string }
    | { kind: "editEaterGroup"; id: string }
    | { kind: "addMembers"; eaterGroupId: string }
    | { kind: "makeEaters" }
    | { kind: "repairGroups" };

export interface SetupIssue {
    key: string;
    severity: "error" | "warning";
    text: string;
    /** Users the issue is about; each can be opened. */
    emails?: string[];
    action?: IssueAction;
    /** Overrides the default button text of the action. */
    actionLabel?: string;
}

/** Accounts placed in one of the company's eater groups. */
export const membersOfCompany = (accounts: Account[], companyId: string) =>
    accounts.filter(a => !!a.user?.eaterGroupId && a.companyId === companyId);

export const notEater = (a: Account) => !!a.serviceUser && !!a.user?.eaterGroupId && !a.groups.includes(GROUPS.eater);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Everything that is missing or wrong for a company to work: admins, offices, eater groups and members
 * that cannot book. Errors stop people from using PlanEat, warnings should be looked at.
 */
export function companyIssues(data: SysadminData, accounts: Account[], companyId: string): SetupIssue[] {
    const company = data.companyById(companyId);
    if (!company) return [];
    const issues: SetupIssue[] = [];
    const offices = data.offices.map(o => o.content).filter(o => o.companyId === companyId);
    const eaterGroups = data.eaterGroups.map(g => g.content).filter(g => offices.some(o => o.id === g.officeId));
    const members = membersOfCompany(accounts, companyId);

    if (!(company.companyAdminIds ?? []).length) {
        issues.push({ key: "companyAdmin", severity: "warning", text: "The company has no admin", action: { kind: "editCompany", id: companyId } });
    }
    if (offices.length === 0) {
        issues.push({ key: "noOffices", severity: "error", text: "The company has no offices, so nobody can book", action: { kind: "addOffice" } });
    }
    offices.forEach(office => {
        const name = office.officeName || "Unnamed office";
        if (!(office.officeAdminIds ?? []).length) {
            issues.push({ key: "officeAdmin" + office.id, severity: "warning", text: `Office "${name}" has no admin`, action: { kind: "editOffice", id: office.id } });
        }
        if (!eaterGroups.some(g => g.officeId === office.id)) {
            issues.push({ key: "noGroups" + office.id, severity: "error", text: `Office "${name}" has no eater groups, so nobody can book there`, action: { kind: "addEaterGroup", officeId: office.id } });
        }
    });
    eaterGroups.forEach(group => {
        const name = group.eaterGroupName || "Unnamed eater group";
        if (!(group.eaterGroupAdminIds ?? []).length) {
            issues.push({ key: "groupAdmin" + group.id, severity: "warning", text: `Eater group "${name}" has no admin`, action: { kind: "editEaterGroup", id: group.id } });
        }
        if (!members.some(a => a.user?.eaterGroupId === group.id)) {
            issues.push({ key: "noMembers" + group.id, severity: "warning", text: `Eater group "${name}" has no members`, action: { kind: "addMembers", eaterGroupId: group.id } });
        }
    });

    const adminIds = [
        ...(company.companyAdminIds ?? []),
        ...offices.flatMap(o => o.officeAdminIds ?? []),
        ...eaterGroups.flatMap(g => g.eaterGroupAdminIds ?? []),
    ];
    issues.push(...peopleIssues(data, accounts, members, adminIds));

    const order = { error: 0, warning: 1 };
    return issues.sort((a, b) => order[a.severity] - order[b.severity]);
}

/** Problems with the members and admins: missing logins, members that cannot book and broken user groups. */
function peopleIssues(data: SysadminData, accounts: Account[], members: Account[], admins: string[]): SetupIssue[] {
    const issues: SetupIssue[] = [];
    const adminIds = Array.from(new Set(admins));
    const accountIds = new Set(data.serviceUsers.map(s => s.content.id));
    const adminsWithoutLogin = adminIds.filter(id => !accountIds.has(id));
    if (adminsWithoutLogin.length) {
        issues.push({ key: "adminLogin", severity: "warning", text: `${plural(adminsWithoutLogin.length, "admin has", "admins have")} no login account`, emails: adminsWithoutLogin });
    }
    const notEaters = members.filter(notEater);
    if (notEaters.length) {
        issues.push({
            key: "notEaters", severity: "error",
            text: `${plural(notEaters.length, "member is", "members are")} placed in an eater group but cannot book, because they are not eaters`,
            emails: notEaters.map(a => a.email), action: { kind: "makeEaters" },
        });
    }
    const unverified = members.filter(a => a.status === "unverified");
    if (unverified.length) {
        issues.push({ key: "unverified", severity: "warning", text: `${plural(unverified.length, "member has", "members have")} not verified their email and cannot log in`, emails: unverified.map(a => a.email) });
    }
    const missingLogin = members.filter(a => a.status === "missingAccount");
    if (missingLogin.length) {
        issues.push({ key: "missingLogin", severity: "error", text: `${plural(missingLogin.length, "member has", "members have")} no login account`, emails: missingLogin.map(a => a.email) });
    }
    const relatedEmails = new Set([...members.map(a => a.email), ...adminIds]);
    const broken = accounts.filter(a => a.groupsNeedRepair && relatedEmails.has(a.email));
    if (broken.length) {
        issues.push({
            key: "broken", severity: "warning", text: `${plural(broken.length, "user has", "users have")} user groups stored in a broken format`,
            emails: broken.map(a => a.email), action: { kind: "repairGroups" },
        });
    }
    return issues;
}

/** What is missing or wrong for one eater group: admin, members and members that cannot book. */
export function eaterGroupIssues(data: SysadminData, accounts: Account[], eaterGroupId: string): SetupIssue[] {
    const group = data.eaterGroupById(eaterGroupId);
    if (!group) return [];
    const issues: SetupIssue[] = [];
    const members = accounts.filter(a => a.user?.eaterGroupId === eaterGroupId);
    if (!(group.eaterGroupAdminIds ?? []).length) {
        issues.push({ key: "groupAdmin", severity: "warning", text: "The eater group has no admin", action: { kind: "editEaterGroup", id: eaterGroupId }, actionLabel: "Add admin" });
    }
    if (!data.officeById(group.officeId)) {
        issues.push({ key: "office", severity: "error", text: "The eater group's office no longer exists, so nobody can book", action: { kind: "editEaterGroup", id: eaterGroupId }, actionLabel: "Choose office" });
    }
    if (members.length === 0) {
        issues.push({ key: "noMembers", severity: "warning", text: "The eater group has no members", action: { kind: "addMembers", eaterGroupId } });
    }
    issues.push(...peopleIssues(data, accounts, members, group.eaterGroupAdminIds ?? []));
    const order = { error: 0, warning: 1 };
    return issues.sort((a, b) => order[a.severity] - order[b.severity]);
}
